import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Text, Button, Badge, Dialog, DialogTrigger, DialogSurface,
  DialogTitle, DialogBody, DialogActions, DialogContent,
  Input, Dropdown, Option, Field, Combobox, Tag, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { FolderRegular, AddRegular, EditRegular } from '@fluentui/react-icons';
import type { Project, EngagementTypeOption } from '../services/project-service';
import { getBadgeProps, CAF_PHASE_BADGE, PROJECT_STATUS_BADGE } from '../utils/badge-config';

interface ProjectsPageProps {
  projects: Project[];
  engagementTypes: EngagementTypeOption[];
  onCreateProject: (project: Omit<Project, 'id'>) => void;
  onSelectProject: (project: Project) => void;
  onUpdateProject?: (project: Project, options: { newEngagementTypes: string[] }) => Promise<void> | void;
  onRescaffoldProject?: (project: Project) => Promise<void> | void;
}

export function ProjectsPage({
  projects,
  engagementTypes,
  onCreateProject,
  onSelectProject,
  onUpdateProject,
  onRescaffoldProject,
}: ProjectsPageProps) {
  const intl = useIntl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [editEngagementTypes, setEditEngagementTypes] = useState<string[]>([]);
  const [editOriginalTypes, setEditOriginalTypes] = useState<string[]>([]);
  const [showRescaffoldBanner, setShowRescaffoldBanner] = useState(false);
  const [rescaffolding, setRescaffolding] = useState(false);

  const blankProject = {
    name: '',
    customerId: '',
    scope: 'Azure Migrate Assessment',
    azureRegion: 'South Africa North',
    hypervisor: 'Hyper-V',
    vpnType: 'site-to-site',
    engagementTypes: [] as string[],
  };
  const [newProject, setNewProject] = useState(blankProject);

  const engagementTypeLabel = (id: string) =>
    engagementTypes.find(t => t.id === id)?.displayName ?? id;

  const handleCreate = () => {
    onCreateProject({
      ...newProject,
      status: 'active',
      cafPhase: 'strategy',
    });
    setDialogOpen(false);
    setNewProject(blankProject);
  };

  const openEditDialog = (project: Project) => {
    setEditProject(project);
    const current = project.engagementTypes ?? [];
    setEditEngagementTypes(current);
    setEditOriginalTypes(current);
    setShowRescaffoldBanner(false);
  };

  const closeEditDialog = () => {
    setEditProject(null);
    setEditEngagementTypes([]);
    setEditOriginalTypes([]);
    setShowRescaffoldBanner(false);
    setRescaffolding(false);
  };

  const handleSaveEdit = async () => {
    if (!editProject || !onUpdateProject) return;
    const before = new Set(editOriginalTypes);
    const after = new Set(editEngagementTypes);
    const addedTypes = [...after].filter(t => !before.has(t));
    const arrayChanged =
      addedTypes.length > 0 || [...before].some(t => !after.has(t));

    await onUpdateProject(
      { ...editProject, engagementTypes: editEngagementTypes },
      { newEngagementTypes: addedTypes },
    );

    if (arrayChanged && addedTypes.length > 0) {
      // Keep the dialog open and surface the re-scaffold prompt.
      setEditOriginalTypes(editEngagementTypes);
      setShowRescaffoldBanner(true);
    } else {
      closeEditDialog();
    }
  };

  const handleConfirmRescaffold = async () => {
    if (!editProject || !onRescaffoldProject) return;
    setRescaffolding(true);
    try {
      await onRescaffoldProject({ ...editProject, engagementTypes: editEngagementTypes });
    } finally {
      setRescaffolding(false);
      closeEditDialog();
    }
  };

  return (
    <div className="page-root page-root--scrollable projects-page">
      <div className="page-title-bar">
        <FolderRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'projects.title' })}</Text>
        <div className="title-bar-actions">
          <Dialog open={dialogOpen} onOpenChange={(_e, data) => setDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<AddRegular />}>{intl.formatMessage({ id: 'projects.btn.newProject' })}</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{intl.formatMessage({ id: 'projects.dialog.create.title' })}</DialogTitle>
                <DialogContent>
                  <div className="dialog-form">
                    <Field label={intl.formatMessage({ id: 'projects.field.customerId' })} required>
                      <Input value={newProject.customerId} onChange={(_e, d) => setNewProject(p => ({ ...p, customerId: d.value }))} placeholder="e.g. kzn-health" />
                    </Field>
                    <Field label={intl.formatMessage({ id: 'projects.field.projectName' })} required>
                      <Input value={newProject.name} onChange={(_e, d) => setNewProject(p => ({ ...p, name: d.value }))} placeholder="e.g. KZN Health Azure Migration" />
                    </Field>
                    <Field label={intl.formatMessage({ id: 'projects.field.scope' })}>
                      <Input value={newProject.scope} onChange={(_e, d) => setNewProject(p => ({ ...p, scope: d.value }))} />
                    </Field>
                    <Field
                      label={intl.formatMessage({ id: 'projects.field.engagementType' })}
                      required
                      hint={intl.formatMessage({ id: 'projects.field.engagementType.hint' })}
                      validationState={newProject.engagementTypes.length === 0 ? 'error' : 'none'}
                      validationMessage={
                        newProject.engagementTypes.length === 0
                          ? intl.formatMessage({ id: 'projects.field.engagementType.required' })
                          : undefined
                      }
                    >
                      <Combobox
                        multiselect
                        placeholder={intl.formatMessage({ id: 'projects.field.engagementType.placeholder' })}
                        selectedOptions={newProject.engagementTypes}
                        value={newProject.engagementTypes.map(engagementTypeLabel).join(', ')}
                        onOptionSelect={(_e, data) =>
                          setNewProject(p => ({ ...p, engagementTypes: data.selectedOptions }))
                        }
                      >
                        {engagementTypes.map(t => (
                          <Option key={t.id} value={t.id}>{t.displayName}</Option>
                        ))}
                      </Combobox>
                    </Field>
                    <Field label={intl.formatMessage({ id: 'projects.field.azureRegion' })}>
                      <Dropdown value={newProject.azureRegion} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, azureRegion: d.optionValue ?? '' }))}>
                        <Option value="South Africa North">South Africa North</Option>
                        <Option value="West Europe">West Europe</Option>
                        <Option value="North Europe">North Europe</Option>
                        <Option value="UK South">UK South</Option>
                      </Dropdown>
                    </Field>
                    <Field label={intl.formatMessage({ id: 'projects.field.hypervisor' })}>
                      <Dropdown value={newProject.hypervisor} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, hypervisor: d.optionValue ?? '' }))}>
                        <Option value="Hyper-V">Hyper-V</Option>
                        <Option value="VMware">VMware</Option>
                      </Dropdown>
                    </Field>
                    <Field label={intl.formatMessage({ id: 'projects.field.vpnType' })}>
                      <Dropdown value={newProject.vpnType} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, vpnType: d.optionValue ?? '' }))}>
                        <Option value="site-to-site">{intl.formatMessage({ id: 'projects.vpnType.siteToSite' })}</Option>
                        <Option value="expressroute">{intl.formatMessage({ id: 'projects.vpnType.expressRoute' })}</Option>
                        <Option value="direct-internet">{intl.formatMessage({ id: 'projects.vpnType.directInternet' })}</Option>
                      </Dropdown>
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{intl.formatMessage({ id: 'projects.btn.cancel' })}</Button>
                  </DialogTrigger>
                  <Button
                    appearance="primary"
                    onClick={handleCreate}
                    disabled={!newProject.name || !newProject.customerId || newProject.engagementTypes.length === 0}
                  >
                    {intl.formatMessage({ id: 'projects.btn.create' })}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </div>

      {/* Edit dialog */}
      {editProject && (
        <Dialog open onOpenChange={(_e, data) => { if (!data.open) closeEditDialog(); }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{intl.formatMessage({ id: 'projects.dialog.edit.title' }, { name: editProject.name })}</DialogTitle>
              <DialogContent>
                <div className="dialog-form">
                  <Field
                    label={intl.formatMessage({ id: 'projects.field.engagementType' })}
                    required
                    validationState={editEngagementTypes.length === 0 ? 'error' : 'none'}
                    validationMessage={
                      editEngagementTypes.length === 0
                        ? intl.formatMessage({ id: 'projects.field.engagementType.requiredEdit' })
                        : undefined
                    }
                  >
                    <Combobox
                      multiselect
                      placeholder={intl.formatMessage({ id: 'projects.field.engagementType.placeholder' })}
                      selectedOptions={editEngagementTypes}
                      value={editEngagementTypes.map(engagementTypeLabel).join(', ')}
                      onOptionSelect={(_e, data) => setEditEngagementTypes(data.selectedOptions)}
                    >
                      {engagementTypes.map(t => (
                        <Option key={t.id} value={t.id}>{t.displayName}</Option>
                      ))}
                    </Combobox>
                  </Field>
                  {showRescaffoldBanner && (
                    <MessageBar intent="info">
                      <MessageBarBody>
                        {intl.formatMessage({ id: 'projects.rescaffold.banner' })}
                      </MessageBarBody>
                      <Button
                        appearance="primary"
                        size="small"
                        disabled={rescaffolding}
                        onClick={handleConfirmRescaffold}
                      >
                        {rescaffolding
                          ? intl.formatMessage({ id: 'projects.rescaffold.btn.inProgress' })
                          : intl.formatMessage({ id: 'projects.rescaffold.btn.action' })}
                      </Button>
                    </MessageBar>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeEditDialog}>{intl.formatMessage({ id: 'projects.btn.close' })}</Button>
                {!showRescaffoldBanner && (
                  <Button
                    appearance="primary"
                    onClick={handleSaveEdit}
                    disabled={!onUpdateProject || editEngagementTypes.length === 0}
                  >
                    {intl.formatMessage({ id: 'projects.btn.save' })}
                  </Button>
                )}
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      <div className="table-scroll-container">
        <table className="data-table projects-table">
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: 'projects.col.project' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.customer' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.cafPhase' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.engagementType' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.scope' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.status' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.created' })}</th>
              <th>{intl.formatMessage({ id: 'projects.col.actions' })}</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={8} className="text-muted">{intl.formatMessage({ id: 'projects.empty' })}</td></tr>
            )}
            {projects.map(p => (
              <tr key={p.id} className="clickable">
                <td onClick={() => onSelectProject(p)}><Text weight="semibold">{p.name}</Text></td>
                <td className="text-muted" onClick={() => onSelectProject(p)}>{p.customerId}</td>
                <td onClick={() => onSelectProject(p)}><Badge size="small" {...getBadgeProps(CAF_PHASE_BADGE, p.cafPhase)}>{p.cafPhase}</Badge></td>
                <td onClick={() => onSelectProject(p)}>
                  {(p.engagementTypes ?? []).length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <div className="tag-chip-row">
                      {(p.engagementTypes ?? []).map(id => (
                        <Tag key={id} size="extra-small" appearance="brand">{engagementTypeLabel(id)}</Tag>
                      ))}
                    </div>
                  )}
                </td>
                <td className="text-muted" onClick={() => onSelectProject(p)}>{p.scope}</td>
                <td onClick={() => onSelectProject(p)}><Badge size="small" {...getBadgeProps(PROJECT_STATUS_BADGE, p.status)}>{p.status}</Badge></td>
                <td className="text-muted" onClick={() => onSelectProject(p)}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-ZA') : ''}</td>
                <td>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<EditRegular />}
                    aria-label={intl.formatMessage({ id: 'projects.btn.edit.ariaLabel' }, { name: p.name })}
                    onClick={(e) => { e.stopPropagation(); openEditDialog(p); }}
                    disabled={!onUpdateProject}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
